import {useCallback, useMemo} from 'react'
import {ActivityIndicator, ScrollView, View} from 'react-native'
import {AtUri} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {FEATURE_CAPSULE_POST} from '#/lib/constants'
import {useDemoFamily} from '#/lib/demoFamily'
import {type HubRole, roleOf} from '#/lib/roles'
import {type NavigationProp} from '#/lib/routes/types'
import {
  type CapsuleDraft,
  useCapsuleDraftsQuery,
} from '#/state/queries/capsule-drafts'
import {
  useActorCapsulesQuery,
  useCapsulesByUrisQuery,
  usePlaysByUserQuery,
  usePostThumbnailsQuery,
} from '#/state/queries/hub'
import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {GameController_Stroke2_Corner0_Rounded as GameIcon} from '#/components/icons/GameController'
import {PersonGroup_Stroke2_Corner2_Rounded as PersonGroupIcon} from '#/components/icons/Person'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {CapsuleCard, type CapsuleCardKind} from './components/CapsuleCard'
import {CapsuleRail} from './components/CapsuleRail'
import {
  deriveCreatorSections,
  deriveLearnerSections,
  type HubCapsule,
  type LearnerItem,
} from './hubPolicy'

function useOpenCapsulePlay() {
  const navigation = useNavigation<NavigationProp>()
  return useCallback(
    (capsule: HubCapsule) => {
      // CapsulePlay resolves capsules through their video post, and a
      // capsule record and its post have different rkeys — so navigate with
      // the post's rkey or playback fails with "can't be played".
      const uri = capsule.postUri ?? capsule.uri
      if (!uri || uri.startsWith('draft:')) return
      navigation.navigate('CapsulePlay', {
        capsuleid: new AtUri(uri).rkey,
        autoPlay: true,
        authorDid: capsule.creatorDid,
        source: 'feed',
      })
    },
    [navigation],
  )
}

function LearnerCardList({
  items,
  thumbnailByUri,
}: {
  items: LearnerItem[]
  thumbnailByUri: Record<string, string | undefined>
}) {
  const openCapsule = useOpenCapsulePlay()
  return (
    <>
      {items.map(item => {
        const kind: CapsuleCardKind =
          item.play.status === 'started'
            ? {kind: 'continue'}
            : item.pct !== undefined && item.pct < 50
              ? {kind: 'boost', pct: item.pct}
              : {kind: 'history', pct: item.pct}
        return (
          <CapsuleCard
            key={item.capsule.uri}
            capsule={item.capsule}
            kind={kind}
            thumbnailUri={thumbnailByUri[item.capsule.uri]}
            onPress={() => openCapsule(item.capsule)}
          />
        )
      })}
    </>
  )
}

function useThumbnailByCapsuleUri(
  capsules: HubCapsule[] | undefined,
  thumbnails: Record<string, string> | undefined,
) {
  return useMemo(() => {
    const out: Record<string, string | undefined> = {}
    if (!capsules || !thumbnails) return out
    for (const capsule of capsules) {
      if (capsule.postUri && thumbnails[capsule.postUri]) {
        out[capsule.uri] = thumbnails[capsule.postUri]
      }
    }
    return out
  }, [capsules, thumbnails])
}

function LearnerHub() {
  const {t} = useLingui()
  const {data: plays, isLoading: playsLoading} = usePlaysByUserQuery()
  const capsuleUris = useMemo(
    () => (plays ?? []).map(p => p.capsuleUri),
    [plays],
  )
  const {data: capsuleByUri, isLoading: capsulesLoading} =
    useCapsulesByUrisQuery(capsuleUris)

  const postUris = useMemo(() => {
    const byUri = capsuleByUri ?? {}
    return Object.values(byUri)
      .map(c => c.postUri)
      .filter((u): u is string => !!u)
  }, [capsuleByUri])
  const {data: thumbnails} = usePostThumbnailsQuery(postUris)
  const thumbnailByUri = useThumbnailByCapsuleUri(
    Object.values(capsuleByUri ?? {}),
    thumbnails,
  )

  const sections = useMemo(
    () => deriveLearnerSections(plays ?? [], capsuleByUri ?? {}),
    [plays, capsuleByUri],
  )

  if (playsLoading || capsulesLoading) {
    return <HubLoading />
  }
  const hasAny =
    sections.continueItems.length +
      sections.boostItems.length +
      sections.historyItems.length >
    0
  if (!hasAny) {
    return (
      <View style={[a.px_lg, a.py_2xl, a.align_center, a.gap_sm]}>
        <Text style={[a.text_2xl, a.font_bold, a.text_center]}>
          {t`No capsule plays yet`}
        </Text>
        <Text style={[a.text_md, a.text_center]}>
          {t`Capsules you start, finish, or replay will show up here so you can keep going and boost your scores.`}
        </Text>
      </View>
    )
  }

  return (
    <View>
      {sections.continueItems.length > 0 && (
        <CapsuleRail
          title={t`Keep going`}
          subtitle={t`Started but not finished`}>
          <LearnerCardList
            items={sections.continueItems}
            thumbnailByUri={thumbnailByUri}
          />
        </CapsuleRail>
      )}
      {sections.boostItems.length > 0 && (
        <CapsuleRail
          title={t`Boost your score`}
          subtitle={t`Finished below 50% — replay to improve`}>
          <LearnerCardList
            items={sections.boostItems}
            thumbnailByUri={thumbnailByUri}
          />
        </CapsuleRail>
      )}
      {sections.historyItems.length > 0 && (
        <CapsuleRail title={t`Recently played`}>
          <LearnerCardList
            items={sections.historyItems}
            thumbnailByUri={thumbnailByUri}
          />
        </CapsuleRail>
      )}
    </View>
  )
}

function draftToHubCapsule(draft: CapsuleDraft, did: string): HubCapsule {
  return {
    uri: `draft:${draft.id}`,
    title: draft.capsule.title,
    status: 'draft',
    themeId: draft.capsule.themeId,
    subject: draft.capsule.subject,
    level: draft.capsule.level,
    indexedAt: draft.updatedAt,
    creatorDid: did,
    slides: draft.capsule.slides ?? [],
  }
}

function CreatorHub({did}: {did: string}) {
  const {t} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {data: drafts, isLoading: draftsLoading} = useCapsuleDraftsQuery()
  const {data: published, isLoading: publishedLoading} = useActorCapsulesQuery(
    did,
    'published',
  )
  const openCapsule = useOpenCapsulePlay()

  const draftCapsules = useMemo(
    () => (drafts ?? []).map(d => draftToHubCapsule(d, did)),
    [drafts, did],
  )

  const publishedPostUris = useMemo(
    () => (published ?? []).map(c => c.postUri).filter((u): u is string => !!u),
    [published],
  )
  const {data: thumbnails} = usePostThumbnailsQuery(publishedPostUris)
  const thumbnailByUri = useThumbnailByCapsuleUri(published ?? [], thumbnails)

  const sections = useMemo(
    () => deriveCreatorSections([...draftCapsules, ...(published ?? [])]),
    [draftCapsules, published],
  )

  if (draftsLoading || publishedLoading) return <HubLoading />

  return (
    <View>
      <CapsuleRail
        title={t`Your drafts`}
        subtitle={t`Pick up where you left off`}
        emptyText={t`No drafts — start a new capsule and save as you go`}>
        {sections.drafts.map(c => {
          const id = c.uri.startsWith('draft:') ? c.uri.slice(6) : undefined
          return (
            <CapsuleCard
              key={c.uri}
              capsule={c}
              kind={{kind: 'draft'}}
              onPress={
                FEATURE_CAPSULE_POST
                  ? () => navigation.navigate('CapsuleStudio', {draftId: id})
                  : undefined
              }
            />
          )
        })}
      </CapsuleRail>
      <CapsuleRail
        title={t`Most played`}
        subtitle={t`Your learners keep coming back`}
        emptyText={t`Plays will appear once learners finish your capsules`}>
        {sections.mostPlayed.map(c => (
          <CapsuleCard
            key={c.uri}
            capsule={c}
            kind={{kind: 'mostPlayed', playCount: c.playCount ?? 0}}
            thumbnailUri={thumbnailByUri[c.uri]}
            onPress={() => openCapsule(c)}
          />
        ))}
      </CapsuleRail>
      <CapsuleRail
        title={t`Recently published`}
        emptyText={t`Nothing published yet`}>
        {sections.published.map(c => (
          <CapsuleCard
            key={c.uri}
            capsule={c}
            kind={{kind: 'published'}}
            thumbnailUri={thumbnailByUri[c.uri]}
            onPress={() => openCapsule(c)}
          />
        ))}
      </CapsuleRail>
    </View>
  )
}

/** Family management moved to Settings › Account › Family. */
function ParentHub() {
  const {t} = useLingui()
  const theme = useTheme()
  const navigation = useNavigation<NavigationProp>()
  return (
    <View style={[a.px_xl, a.py_5xl, a.align_center, a.gap_md]}>
      <PersonGroupIcon
        size="xl"
        fill={theme.atoms.text_contrast_medium.color}
      />
      <Text style={[a.text_lg, a.font_bold, a.text_center]}>
        {t`No family linked yet`}
      </Text>
      <Text
        style={[a.text_md, a.text_center, theme.atoms.text_contrast_medium]}>
        {t`Add your children in Settings › Account › Family to follow their learning and suggest replays.`}
      </Text>
      <Button
        label={t`Open Family settings`}
        size="small"
        color="primary"
        style={[a.mt_md]}
        onPress={() => navigation.navigate('FamilySettings')}>
        <ButtonText>{t`Open Family settings`}</ButtonText>
      </Button>
    </View>
  )
}

function HubLoading() {
  return (
    <View style={[a.flex_1, a.align_center, a.justify_center, a.py_5xl]}>
      <ActivityIndicator />
    </View>
  )
}

function HubContent({role, did}: {role: HubRole; did: string}) {
  const {t} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  if (role === 'parent') {
    return (
      <ScrollView contentContainerStyle={[a.p_lg, a.pb_5xl]}>
        <ParentHub />
      </ScrollView>
    )
  }

  const hero =
    role === 'creator' && FEATURE_CAPSULE_POST ? (
      <Button
        label={t`Create a new capsule`}
        size="large"
        color="primary"
        style={[a.rounded_full]}
        onPress={() => navigation.navigate('CapsuleStudio')}>
        <ButtonIcon icon={GameIcon} />
        <ButtonText>{t`New Capsule`}</ButtonText>
      </Button>
    ) : null

  return (
    <ScrollView contentContainerStyle={[a.p_lg, a.pb_5xl]}>
      {hero ? <View style={[a.pb_lg]}>{hero}</View> : null}
      {role === 'creator' ? <CreatorHub did={did} /> : <LearnerHub />}
    </ScrollView>
  )
}

export function HubScreen() {
  const {currentAccount} = useSession()
  const {data: profile, isLoading} = useProfileQuery({
    did: currentAccount?.did,
  })
  const navigation = useNavigation<NavigationProp>()
  const {t} = useLingui()
  const demo = useDemoFamily()

  const onOpenFamily = useCallback(() => {
    navigation.navigate('FamilySettings')
  }, [navigation])

  if (!currentAccount || isLoading) {
    return (
      <Layout.Screen>
        <Layout.Center style={[a.flex_1]}>
          <HubLoading />
        </Layout.Center>
      </Layout.Screen>
    )
  }

  const accountType = (profile as unknown as {accountType?: string})
    ?.accountType
  const role = roleOf(accountType)
  const heading =
    role === 'creator'
      ? t`Creator Hub`
      : role === 'parent'
        ? t`Family Hub`
        : t`Learner Hub`

  // Learners and parents get a header CTA into Family settings; creators keep
  // the in-content "New Capsule" hero instead. When the family switch is OFF
  // (demo) the CTA is deactivated — no invite surface is offered until the
  // user re-enables it in Settings › Account › Family.
  const familyActive = !demo.enabled || demo.familyEnabled
  const headerCtaLabel = !familyActive
    ? null
    : role === 'parent'
      ? t`Add a child`
      : role === 'learner'
        ? t`Invite a parent`
        : null

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content align="left">
          <Layout.Header.TitleText>{heading}</Layout.Header.TitleText>
        </Layout.Header.Content>
        {headerCtaLabel ? (
          <Button
            label={headerCtaLabel}
            color="secondary"
            size="small"
            onPress={onOpenFamily}>
            <ButtonIcon icon={PersonGroupIcon} />
            <ButtonText>{headerCtaLabel}</ButtonText>
          </Button>
        ) : (
          <Layout.Header.Slot />
        )}
      </Layout.Header.Outer>
      <HubContent role={role} did={currentAccount.did} />
    </Layout.Screen>
  )
}

export default HubScreen
